import { Injectable } from '@angular/core';
import { LocalStorageService } from '@app/core/local-storage/local-storage.service';
import { EventConfig, EventLog, EventSession } from './event.model';
import { v4 } from 'uuid';
import { EventRest, EventsClient, LogEventsRequest } from '@app/clients/client.generated';
import { Observable, Subject, bufferTime } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class EventService {

    private readonly LOCAL_STORAGE_KEY = 'event-session';
    private eventSubject = new Subject<EventRest>();

    constructor(private localStorageService: LocalStorageService, private eventsClient: EventsClient) {
        this.getSessionId();
        this.eventSubject
            .pipe(bufferTime(10000))
            .subscribe(events => {
                if (events.length > 0) {
                    const request = new LogEventsRequest({ events: events });
                    this.eventsClient.logEvents(request).subscribe();
                }
            });
    }

    log<T extends EventConfig>(event: EventLog<T>) {
        const eventRest = new EventRest({
            eventId: v4(),
            sessionId: this.getSessionId(),
            eventType: event.type,
            data: event.props,
            occurredAt: new Date()
        });
        this.eventSubject.next(eventRest);
    }

    /**
     * Sends a single event immediately (no buffering).
     * Intended for cases where a full page navigation/reload is about to occur.
     */
    logImmediate<T extends EventConfig>(event: EventLog<T>): Observable<any> {
        const eventRest = new EventRest({
            eventId: v4(),
            sessionId: this.getSessionId(),
            eventType: event.type,
            data: event.props,
            occurredAt: new Date()
        });

        const request = new LogEventsRequest({ events: [eventRest] });
        return this.eventsClient.logEvents(request);
    }

    private getSessionId() {
        const currentSession: EventSession = this.localStorageService.getItem(this.LOCAL_STORAGE_KEY, false, true);
        if (this.isSessionValid(currentSession)) {
            return currentSession.sessionId;
        }
        return this.createSession();
    }

    private isSessionValid = (session: EventSession) => !!session && session.expiresAt > Date.now();

    private createSession() {
        const twoHours =  2 * 60 * 60 * 1000;
        const session: EventSession = {
            sessionId: v4(),
            expiresAt: Date.now() + twoHours
        };
        this.localStorageService.setItem(this.LOCAL_STORAGE_KEY, session, false, true);
        return session.sessionId;
    }
}
